/**
 * Phase 66 Plan 02 — API fetch wrapper.
 *
 * Port of `js/api.js` (Phase 65 vanilla SPA) preserving every invariant:
 *
 *   1. `credentials: "omit"` — load-bearing per js/api.js comments lines 25-32.
 *      Avoids the protos_session cookie from /dashboard tripping CSRFMiddleware
 *      which returns text/plain "CSRF token missing" and breaks JSON.parse on
 *      every API POST. Bearer auth + no cookies = clean separation.
 *
 *   2. 401 → trigger AuthProvider logout via authBus, throw "Sessione scaduta".
 *      authBus avoids the circular import between this module and AuthProvider.
 *
 *   3. 429 → trigger RateLimitBanner via rateLimitStore, throw with
 *      `err.status = 429` (Phase 34 Plan 04 sticky banner contract).
 *
 *   4. Non-2xx → res.json() body, throw Error(body.detail || `Errore ${status}`)
 *      with `err.status` and `err.body` populated. Every page module's
 *      `.catch(err => esc(err.message))` expects this shape.
 *
 *   5. 204 → return null.
 *
 *   6. `raw: true` → return Response unparsed. Used for blob downloads
 *      (vpn-operators .conf, pg-export streamed CSV).
 *
 * @see PATTERNS.md §`src/api/client.ts`
 * @see RESEARCH.md §Pitfall 11 (XSS surface) — JSX escapes by default
 */

import { authBus } from "@/auth/authBus";
import { rateLimitStore } from "@/stores/rateLimitStore";

const API_BASE = "/api/v1";

export type RequestOpts = {
  method?: string;
  body?: unknown;
  raw?: boolean;
  signal?: AbortSignal;
  headers?: HeadersInit;
  /**
   * Opt out of the global 401 → authBus("logout") dispatch. Used by
   * `/auth/login` itself: a 401 there means "wrong credentials", not
   * "session expired" — see js/app.js lines 62-66 (legacy login uses
   * raw fetch and surfaces body.detail directly). Without this flag
   * the very first failed login would funnel through the same logout
   * pipeline as a stale session, hiding the real "Login fallito"
   * message under a generic "Sessione scaduta".
   */
  skipAuthLogout?: boolean;
};

export type ApiError = Error & {
  status?: number;
  body?: unknown;
};

function getToken(): string | null {
  return localStorage.getItem("protos_token");
}

function buildHeaders(opts: RequestOpts): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // Only set Content-Type for JSON bodies. FormData / Blob set their own.
  const body = opts.body;
  const isJsonBody =
    body !== undefined && !(body instanceof FormData) && !(body instanceof Blob);
  if (isJsonBody) headers["Content-Type"] = "application/json";
  // Caller-supplied headers always win.
  if (opts.headers) {
    const supplied = new Headers(opts.headers);
    supplied.forEach((value, key) => {
      headers[key] = value;
    });
  }
  return headers;
}

function buildBody(opts: RequestOpts): BodyInit | undefined {
  const body = opts.body;
  if (body === undefined) return undefined;
  if (body instanceof FormData) return body;
  if (body instanceof Blob) return body;
  return JSON.stringify(body);
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOpts = {},
): Promise<T> {
  const res = await fetch(API_BASE + path, {
    method: opts.method ?? "GET",
    // Load-bearing — see file header (1).
    credentials: "omit",
    headers: buildHeaders(opts),
    body: buildBody(opts),
    signal: opts.signal,
  });

  // 401 → logout via authBus (decoupled from AuthProvider).
  // Login endpoint opts out via skipAuthLogout: a 401 there is "wrong
  // credentials", not "session expired".
  if (res.status === 401) {
    if (!opts.skipAuthLogout) {
      authBus.dispatch("logout");
      const err: ApiError = Object.assign(new Error("Sessione scaduta"), { status: 401 });
      throw err;
    }
    // Fall through to the generic non-2xx body parser below to surface
    // the real backend `detail` (e.g., "Login fallito").
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof (body as { detail?: unknown }).detail === "string"
        ? ((body as { detail: string }).detail as string)
        : "Errore 401";
    const err: ApiError = Object.assign(new Error(detail), { status: 401, body });
    throw err;
  }

  // 429 → rate-limit banner. Read body before throw so caller gets err.body.
  if (res.status === 429) {
    const retryAfter = parseInt(res.headers.get("Retry-After") ?? "10", 10);
    rateLimitStore.trigger(Number.isFinite(retryAfter) ? retryAfter : 10);
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof (body as { detail?: unknown }).detail === "string"
        ? ((body as { detail: string }).detail as string)
        : "Rate limit";
    const err: ApiError = Object.assign(new Error(detail), { status: 429, body });
    throw err;
  }

  // raw=true short-circuits: caller handles streaming / non-JSON.
  if (opts.raw) return res as unknown as T;

  // 204 No Content.
  if (res.status === 204) return null as T;

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      typeof (body as { detail?: unknown }).detail === "string"
        ? ((body as { detail: string }).detail as string)
        : `Errore ${res.status}`;
    const err: ApiError = Object.assign(new Error(detail), { status: res.status, body });
    throw err;
  }

  return (await res.json()) as T;
}

export const apiGet = <T = unknown>(path: string, opts?: RequestOpts) =>
  apiRequest<T>(path, { ...opts, method: "GET" });

export const apiPost = <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
  apiRequest<T>(path, { ...opts, method: "POST", body });

export const apiPut = <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
  apiRequest<T>(path, { ...opts, method: "PUT", body });

export const apiDel = <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
  apiRequest<T>(path, { ...opts, method: "DELETE", body });

export const apiPatch = <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
  apiRequest<T>(path, { ...opts, method: "PATCH", body });

export { API_BASE };
